import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  FormControl } from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import * as moment from 'moment'; // TODO: consider creating date/time handling service
import { PrimaryFunctions } from '@xbs/xbs-enums';

import {
  EntityValidationErrorResponse,
  Entity,
  ParentEntity,
  PrimaryFunction,
  Container,
  Country,
  Currency,
  Message,
  EntityModalPayload } from '../../shared/models';
import { CurrencyScale } from '../../shared/models/enums/currency-scale';

import { ModalService } from '../../shared/modules/modal/modal.service';
import {
  EntitiesService,
  PrimaryFunctionsService,
  CurrenciesService,
  SessionService,
  NotificationService } from '../../shared/services';

import {
  ConfirmationModalComponent
} from '../../shared/components/confirmation-modal/confirmation-modal.component';
import {
  ClosableModalWithErrorResponseModel
} from '../../shared/components/base/closable-modal-with-error-response-model';

@Component({
  selector: 'entity-modal',
  templateUrl: './entity-modal.component.html',
})
export class EntityModalComponent
  extends ClosableModalWithErrorResponseModel<EntityValidationErrorResponse>
  implements OnInit, OnDestroy {

  data: EntityModalPayload = {
    upe: false,
  } as EntityModalPayload;
  entity: Entity;
  entities: Entity[];
  parentEntities: ParentEntity[] = [new ParentEntity(), new ParentEntity(), new ParentEntity()];
  primaryFunctions: PrimaryFunction[] = [];
  domiciles: Country[] = [];
  taxJurisdictions: Country[] = [];
  currencies: Currency[];
  lockedFields: string[] = [];
  workingContainer: Container;
  taxJurisdictionForSelectedDomicile: Country;
  selectedDomicile: Country;
  shouldShowOtherPrimaryFunction: Boolean = false;
  primaryFunctionsDropDown: string[] = [PrimaryFunctions.ByName.Distributor.Name, PrimaryFunctions.ByName.ServiceProvider.Name, PrimaryFunctions.ByName.Manufacturer.Name, PrimaryFunctions.ByName.Integrated.Name, PrimaryFunctions.ByName.Other.Name];

  entityForm: FormGroup;

  scaleEnum = [];
  constructor(
    protected notificationService: NotificationService,
    protected modalService: ModalService,
    private _entitiesService: EntitiesService,
    private _primaryFunctionsService: PrimaryFunctionsService,
    private _currenciesService: CurrenciesService,
    private _sessionService: SessionService,
    private _fb: FormBuilder,
    private _dpConfig: BsDatepickerConfig
  ) {
    super(notificationService, modalService);
    this.validationErrors = new EntityValidationErrorResponse();
    this.workingContainer = this._sessionService.getWorkingContainer();
    this.configureDatePicker();
  }

  ngOnInit(): void {
    this.entity = new Entity();
    this.entity.container.containerId = this.workingContainer.containerId;
    this.populateForm();
    this.getPrimaryFunctions();
    this.getCountries();
    this.getEntities();
    if (this.data.upe) {
      this.entity.upe = true;
      this.getCurrencies();
      this.scaleEnum = Object.values(CurrencyScale).filter(key => !isNaN(Number(CurrencyScale[key])));
    }
    if (this.data.entityId > 0) {
      this.getEntity(this.data.entityId);
    }

    this.listenForDomicileValueChanges();
    this.listenForErrorMessages();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  listenForDomicileValueChanges(): void {
    this.entityForm.get('domicile').valueChanges.subscribe((value) => {
      this.selectedDomicile = this.domiciles.find(domicile => domicile.countryId === value);
      const defaultJurisdictionForDomicile = this.selectedDomicile ? this.selectedDomicile.defaultJurisdiction : null;
      const taxJurisdiction = defaultJurisdictionForDomicile || this.selectedDomicile;

      if (taxJurisdiction) {
        this.entityForm.get('taxJurisdiction').setValue(taxJurisdiction.countryId);
        this.taxJurisdictionForSelectedDomicile = taxJurisdiction;
      }
    });
  }

  selectPrimaryFunction() {
    if (this.entityForm.get('primaryFunction').value === PrimaryFunctions.ByName.Other.Id) {
      this.shouldShowOtherPrimaryFunction = true;
      this.entityForm.addControl('otherPrimaryFunction', new FormControl('', [
        Validators.required,
        Validators.maxLength(75)]));
    } else {
      this.entityForm.removeControl('otherPrimaryFunction');
      this.shouldShowOtherPrimaryFunction = false;
    }
  }

  getSelectedTaxJurisdictionName(): string {
    return this.taxJurisdictionForSelectedDomicile ?
      this.taxJurisdictionForSelectedDomicile.name + `(${this.taxJurisdictionForSelectedDomicile.isoCode})` : '';
  }

  getSelectedDomicileLabel(): string {
    return this.selectedDomicile ?
      `${this.selectedDomicile.name} (${this.selectedDomicile.isoCode})` :
      '';
  }

  saveAndNew(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.formToModel();
    this._entitiesService.save(this.entity).subscribe(() => {
      this.resetState();
      this.getEntities(); // refresh parents list
      this.success();
      this.isSaveDisabled = false;
    });
  }

  save(): void {
    if (this.isSaveDisabled) {
      return;
    }

    this.isSaveDisabled = true;

    this.responseErrors = [];
    this.validationErrors = new EntityValidationErrorResponse();
    this.formToModel();
    if (!this.data.entityId) {
      this._entitiesService.save(this.entity).subscribe(({ data }) => {
        this.updateWorkingContainer();
        this.closeAllModals();
        this.success(data);
      });
    } else {
      this._entitiesService.edit(this.entity.entityId, this.entity).subscribe(({ data }) => {
        this.updateWorkingContainer();
        this.closeAllModals();
        this.success(data);
      });
    }
  }

  close(): void {
    if (this.entityForm.touched) {
      this.modalService.openModal({
        component: ConfirmationModalComponent,
        data: {
          title: 'Close Entity',
          content: 'Are you sure you want to cancel your actions and close it?',
        } as Message,
        success: () => {
          this.closeAllModals();
        },
      });
    } else {
      this.closeAllModals();
    }
  }

  getModalTitle(): string {
    return `${!this.data.entityId ? 'Create ' : 'Edit '}${this.data.upe ? 'UPE' : 'Entity'}`;
  }

  private updateWorkingContainer(): void {
    if (this.data.upe) {
      Object.assign(this.workingContainer, this.entity.container);
      this._sessionService.setWorkingContainer(this.workingContainer);
      this.notificationService.notify(this.workingContainer);
    }
  }

  protected closeAllModals(): void {
    super.closeAllModals();
    this.resetState();
  }

  limitTwoDecimal(event): void {
    const targetValue = event.target.value;
    event.target.value = (targetValue.indexOf('.') >= 0) ? (targetValue.substr(0, targetValue.indexOf('.')) + targetValue.substr(targetValue.indexOf('.'), 3)) : targetValue;
  }

  private populateForm() {
    this.entityForm = this._fb.group({
      code: [this.entity.code,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(20)]],
      fullName: [this.entity.name,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(75)]],
      domicile: [this.entity.domicile.countryId, Validators.required],
      taxJurisdiction: [{ value: this.entity.taxJurisdiction.countryId, disabled: true }, Validators.required],
      primaryFunction: [this.entity.primaryFunction.primaryFunctionId, Validators.required],
      fiscalYearEnd: ['', Validators.required],
      otherPrimaryFunction: ['', [Validators.required, Validators.maxLength(75)]],
      description: [this.entity.description],
    });
    if (!this.data.upe) {
      this.entityForm.addControl('parent1',
        new FormControl(this.parentEntities[0].parent.entityId, Validators.required));
      this.entityForm.addControl('ownership1', new FormControl(this.parentEntities[0].ownership, [
        Validators.required,
        Validators.max(100),
      ]));
      this.entityForm.addControl('parent2', new FormControl(this.parentEntities[1].parent.entityId));
      this.entityForm.addControl('ownership2', new FormControl(this.parentEntities[1].ownership));
      this.entityForm.addControl('parent3', new FormControl(this.parentEntities[2].parent.entityId));
      this.entityForm.addControl('ownership3', new FormControl(this.parentEntities[2].ownership));
    } else {
      this.entityForm.addControl('mneName', new FormControl(this.entity.upeName, [
        Validators.required,
        Validators.minLength(3),
        Validators.maxLength(75)]));
      this.entityForm
        .addControl('upeCurrency', new FormControl(this.entity.upeCurrency.currencyId, Validators.required));
      this.entityForm.addControl(
        'upeCurrencyScale',
        new FormControl(CurrencyScale[this.entity.upeCurrencyScale], Validators.required)
      );
    }
  }

  private modelToForm(): void {
    this.entityForm.get('code').setValue(this.entity.code);
    this.entityForm.get('fullName').setValue(this.entity.name);
    this.entityForm.get('domicile').setValue(this.entity.domicile.countryId);
    this.entityForm.get('taxJurisdiction').setValue(this.entity.taxJurisdiction.countryId);
    this.entityForm.get('primaryFunction').setValue(this.entity.primaryFunction.primaryFunctionId);
    this.entityForm.get('fiscalYearEnd').setValue(moment(this.entity.fiscalYearEnd, 'YYYY-MM-DD').format('DD-MMM-YYYY'));
    this.entityForm.get('otherPrimaryFunction').setValue(this.entity.otherPrimaryFunction);
    this.entityForm.get('description').setValue(this.entity.description);
    if (!this.data.upe) {
      this.entity.parentEntities.forEach((element, index) => {
        this.parentEntities[index] = element;
      });
      this.entityForm.get('parent1').setValue(this.parentEntities[0].parent.entityId);
      this.entityForm.get('parent2').setValue(this.parentEntities[1].parent.entityId);
      this.entityForm.get('parent3').setValue(this.parentEntities[2].parent.entityId);
      this.entityForm.get('ownership1').setValue(Number((this.parentEntities[0].ownership * 100).toFixed(2)));
      this.entityForm.get('ownership2')
        .setValue(!this.parentEntities[1].ownership ?
          this.parentEntities[1].ownership : Number((this.parentEntities[1].ownership * 100).toFixed(2)));
      this.entityForm.get('ownership3').setValue(!this.parentEntities[2].ownership ?
        this.parentEntities[2].ownership : Number((this.parentEntities[2].ownership * 100).toFixed(2)));
    } else {
      this.entityForm.get('mneName').setValue(this.entity.upeName);
      this.entityForm.get('upeCurrency').setValue(this.entity.upeCurrency.currencyId);
      this.entityForm.get('upeCurrencyScale').setValue(CurrencyScale[this.entity.upeCurrencyScale]);
    }

    this.disableFields();
  }

  private disableFields(): void {
    this.lockedFields.forEach((field) => {
      this.entityForm.get(field).disable();
    });
  }

  private formToModel(): void {
    this.entity.code = this.entityForm.get('code').value;
    this.entity.name = this.entityForm.get('fullName').value;
    this.entity.domicile.countryId = this.entityForm.get('domicile').value;
    this.entity.taxJurisdiction.countryId = this.entityForm.get('taxJurisdiction').value;
    this.entity.primaryFunction.primaryFunctionId = this.entityForm.get('primaryFunction').value;
    this.entity.otherPrimaryFunction = this.entityForm.get('primaryFunction').value === PrimaryFunctions.ByName.Other.Id ? this.entityForm.get('otherPrimaryFunction').value : null;
    this.entity.fiscalYearEnd = moment(this.entityForm.get('fiscalYearEnd').value, 'DD-MMM-YYYY').format('YYYY-MM-DD');
    this.entity.description = this.entityForm.get('description').value;
    if (this.data.upe) {
      this.entity.upeName = this.entityForm.get('mneName').value;
      this.entity.upeCurrency.currencyId = this.entityForm.get('upeCurrency').value;
      this.entity.upeCurrencyScale = +CurrencyScale[this.entityForm.get('upeCurrencyScale').value];
      delete this.entity.parentEntities;
    } else {
      delete this.entity.upeCurrencyScale;
      this.entity.parentEntities = [];
      const parent1Id = this.entityForm.get('parent1').value;
      if (!!parent1Id) {
        const parent1 = new ParentEntity();
        parent1.parent.entityId = parent1Id;
        const ownership1Value = this.entityForm.get('ownership1').value.toString();
        parent1.ownership = ownership1Value.indexOf('.') >= 0 ? (ownership1Value.substr(0, ownership1Value.indexOf('.')) + ownership1Value.substr(ownership1Value.indexOf('.'), 3)) / 100 : ownership1Value / 100;
        this.entity.parentEntities.push(parent1);
      }
      const parent2Id = this.entityForm.get('parent2').value;
      if (!!parent2Id) {
        const parent2 = new ParentEntity();
        parent2.parent.entityId = parent2Id;
        const ownership2Value = this.entityForm.get('ownership2').value.toString();
        parent2.ownership = ownership2Value.indexOf('.') >= 0 ? (ownership2Value.substr(0, ownership2Value.indexOf('.')) + ownership2Value.substr(ownership2Value.indexOf('.'), 3)) / 100 : ownership2Value / 100;
        this.entity.parentEntities.push(parent2);
      }
      const parent3Id = this.entityForm.get('parent3').value;
      if (!!parent3Id) {
        const parent3 = new ParentEntity();
        parent3.parent.entityId = parent3Id;
        const ownership3Value = this.entityForm.get('ownership3').value.toString();
        parent3.ownership = ownership3Value.indexOf('.') >= 0 ? (ownership3Value.substr(0, ownership3Value.indexOf('.')) + ownership3Value.substr(ownership3Value.indexOf('.'), 3)) / 100 : ownership3Value / 100;
        this.entity.parentEntities.push(parent3);
      }
    }
  }

  private getEntity(entityId: number): void {
    this.responseErrors = [];
    this._entitiesService.show(entityId).subscribe(({ data, locked }) => {
      this.lockedFields = locked;
      this.entity = data;
      this.modelToForm();
      this.entityForm.get('code').markAsUntouched();
      this.entityForm.get('fullName').markAsUntouched();
      this.selectPrimaryFunction();
    });
  }

  private resetForm() {
    this.entityForm.markAsUntouched();
    this.entityForm.get('code').setValue('');
    this.entityForm.get('fullName').setValue('');
    this.entityForm.get('domicile').setValue('');
    this.entityForm.get('taxJurisdiction').setValue('');
    this.entityForm.get('primaryFunction').setValue('');
    if (this.entityForm.get('primaryFunction').value === PrimaryFunctions.ByName.Other.Id) {
      this.entityForm.get('otherPrimaryFunction').setValue(null);
    }
    if (!this.data.upe) {
      this.entityForm.get('parent1').setValue('');
      this.entityForm.get('ownership1').setValue('');
      this.entityForm.get('parent2').setValue('');
      this.entityForm.get('ownership2').setValue('');
      this.entityForm.get('parent3').setValue('');
      this.entityForm.get('ownership3').setValue('');
    }
    this.entityForm.get('fiscalYearEnd').setValue('');
    this.entityForm.get('description').setValue('');
  }

  private resetState(): void {
    this.entity = new Entity();
    this.entity.container.containerId = this.workingContainer.containerId;
    this.responseErrors = [];
    this.validationErrors = new EntityValidationErrorResponse();
    this.resetForm();
  }

  private getPrimaryFunctions(): void {
    this._primaryFunctionsService.list().subscribe(({ data }) => {
      this.primaryFunctions = data.filter(primaryFunction => this.primaryFunctionsDropDown.includes(primaryFunction.name));
    });
  }

  private getCountries(): void {
    this.domiciles = this._sessionService.getCountries();
    this.taxJurisdictions = this._sessionService.getCountries();
  }

  private getCurrencies(): void {
    this._currenciesService.list().subscribe(({ data }) => {
      this.currencies = data;
    });
  }

  private getEntities(): void {
    this._entitiesService.list(`?sort=code&order=asc`).subscribe(({ data }) => {
      this.entities = data;
      if (!!this.data.entityId) {
        this.entities = this.entities.filter(entity => entity.entityId !== this.data.entityId);
      }
    });
  }

  private configureDatePicker(): void {
    this._dpConfig.dateInputFormat = 'DD-MMM-YYYY';
    this._dpConfig.containerClass = 'theme-default';
    this._dpConfig.showWeekNumbers = false;
    this._dpConfig.minDate = new Date(`01-Jan-${this.workingContainer.taxYear - 1}`);
    this._dpConfig.maxDate = new Date(`31-Dec-${this.workingContainer.taxYear + 1}`);
  }
}
